/** @format */
/**
 * CalendarAnnotazioni.tsx
 *
 * Layout per pagina dedicata (fullscreen).
 * Usato da ViewCalendarAnnotazioni.tsx.
 *
 * Aggiungere a API_ENDPOINTS:
 *   CALENDAR_ANNOTATION_EVENT: `${process.env.REACT_APP_API_URL}/calendar-annotation-events`,
 */

import React, { useState, useEffect, useMemo } from "react";
import { Row, Col, Calendar, Form, Skeleton, Tooltip, Avatar, Badge, Divider, Pagination, Popconfirm } from "antd";
import { LeftCircleOutlined, RightCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import styled from "styled-components";
import { BaseColor } from "../../../theme/maps/Colors";
import { UICard } from "../../ui/Card";
import { UITitle } from "../../ui/Title";
import { UIText } from "../../ui/Text";
import { UIButton } from "../../ui/Button";
import { UIInput } from "../../ui/Input";
import { CustomRangePicker } from "../../layout/SideDialogs/RequestCreate";
import { API_ENDPOINTS } from "../../../constants/api";
import type { AxiosError } from "axios";
import apiClient from "../../../utils/apiClient";
import { showNotification } from "../../../utils/showNotification";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TCalendarEvent {
    id: string;
    description: string;
    date_from: string;
    date_to: string;
    created_at?: string;
}

interface TAddFormValues {
    description: string;
    date: [moment.Moment, moment.Moment];
}

// ─── Utils ────────────────────────────────────────────────────────────────────

function buildEventMap(
    events: TCalendarEvent[]
): Record<string, TCalendarEvent[]> {
    const map: Record<string, TCalendarEvent[]> = {};

    events.forEach(ev => {
        const cursor = moment(ev.date_from);
        const end    = moment(ev.date_to);

        while (cursor.isSameOrBefore(end, "day")) {
            const ds   = cursor.format("YYYY-MM-DD");
            if (!map[ds]) map[ds] = [];
            map[ds].push(ev);
            cursor.add(1, "day");
        }
    });

    return map;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CalendarAnnotation: React.FC = () => {
    const [events,       setEvents]       = useState<TCalendarEvent[]>([]);
    const [isLoading,    setIsLoading]    = useState(false);
    const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
    const [currentPage,  setCurrentPage]  = useState(1);
    const [form]                          = Form.useForm();

    const PAGE_SIZE = 10;

    useEffect(() => { loadEvents(); }, []);

    const loadEvents = () => {
        setIsLoading(true);
        apiClient
            .get(API_ENDPOINTS.CALENDAR_ANNOTATION_EVENT)
            .then(res => { setEvents(res.data); setIsLoading(false); })
            .catch(() => {
                showNotification("error", "Errore", "Impossibile caricare gli eventi");
                setIsLoading(false);
            });
    };

    const eventMap = useMemo(() => buildEventMap(events), [events]);

    const getEventsForDate = (ds: string): TCalendarEvent[] =>
        eventMap[ds] || [];

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleAdd = async (values: TAddFormValues) => {
        setIsLoading(true);
        const payload = {
            description: values.description,
            date_from:   values.date[0].format("YYYY-MM-DD"),
            date_to:     values.date[1].format("YYYY-MM-DD"),
        };
        try {
            await apiClient.post(API_ENDPOINTS.CALENDAR_ANNOTATION_EVENT, payload);
            form.resetFields();
            showNotification("success", "Successo", "Post-it aggiunto");
            setCurrentPage(1);
            loadEvents();
        } catch (err: unknown) {
            const message = (err as AxiosError<{ message?: string }>).response?.data?.message
                ?? (err instanceof Error ? err.message : undefined);
            showNotification(
                "error",
                "Errore",
                message || "Errore durante il salvataggio"
            );
            setIsLoading(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async (id: string) => {
        try {
            await apiClient.delete(`${API_ENDPOINTS.CALENDAR_ANNOTATION_EVENT}/${id}`);

            const remaining = events.filter(ev => ev.id !== id);
            setEvents(remaining);

            const totalAfter = remaining.length;
            const lastPage   = Math.max(1, Math.ceil(totalAfter / PAGE_SIZE));
            if (currentPage > lastPage) setCurrentPage(lastPage);

            if (selectedDate) {
                const updatedMap = buildEventMap(remaining);
                if (!updatedMap[selectedDate]?.length) setSelectedDate(undefined);
            }

            showNotification("success", "Eliminato", "Post-it rimosso");
        } catch (err: unknown) {
            const message = (err as AxiosError<{ message?: string }>).response?.data?.message
                ?? (err instanceof Error ? err.message : undefined);
            showNotification(
                "error",
                "Errore",
                message || "Errore durante il salvataggio"
            );
        }
    };

    const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

    // ─── Layout ───────────────────────────────────────────────────────────────
    //
    //  ┌── Col 14 ────────────────┐  ┌── Col 10 ─────────────────────────────┐
    //  │  Calendario              │  │  UICard "Aggiungi post-it" (form) │
    //  │                          │  │  ─────────────────────────────────    │
    //  │                          │  │  UICard "Giorno selezionato" (eventi) │
    //  │                          │  │  (visibile solo se data selezionata)  │
    //  └──────────────────────────┘  └───────────────────────────────────────┘
    //  ┌── Col 24 ──────────────────────────────────────────────────────────────┐
    //  │  UICard "Tutti i post-it" (lista cronologica completa)            │
    //  └────────────────────────────────────────────────────────────────────────┘

    return (
        <Row gutter={[24, 24]}>

            {/* ──────────────── Calendario ──────────────────────────────────── */}
            <Col xs={24} lg={14}>
                <UICard style={{ minHeight: "460px" }}>
                    {isLoading ? (
                        <Skeleton active />
                    ) : (
                        <CustomAnnotazioniCalendar
                            fullscreen={false}
                            //@ts-ignore
                            headerRender={({ value, onChange }) => {
                                const cur = moment(value.clone());
                                return (
                                    <Row justify="center" style={{ padding: "8px 0 12px" }}>
                                        <span
                                            className="back-arrow"
                                            onClick={() => onChange(cur.clone().subtract(1, "month"))}
                                        >
                                            <LeftCircleOutlined />
                                        </span>
                                        <span
                                            className="month-name"
                                            data-date={cur.format("YYYY-MM-DD")}
                                        >
                                            {cur.format("MMMM YYYY")}
                                        </span>
                                        <span
                                            className="next-arrow"
                                            onClick={() => onChange(cur.clone().add(1, "month"))}
                                        >
                                            <RightCircleOutlined />
                                        </span>
                                    </Row>
                                );
                            }}
                            dateFullCellRender={(value: moment.Moment) => {
                                const ds     = value.format("YYYY-MM-DD");
                                const dayEvs = getEventsForDate(ds);
                                const isSel  = ds === selectedDate;

                                if (!dayEvs.length) {
                                    return (
                                        <DayCell $selected={isSel}>
                                            {value.format("DD")}
                                        </DayCell>
                                    );
                                }

                                const tooltipContent = (
                                    <div>
                                        {dayEvs.map(ev => (
                                            <div key={ev.id} style={{ padding: "2px 0" }}>
                                                • {ev.description}
                                            </div>
                                        ))}
                                    </div>
                                );

                                return (
                                    <Tooltip title={tooltipContent}>
                                        <span
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                                setSelectedDate(isSel ? undefined : ds)
                                            }
                                        >
                                            {dayEvs.length > 1 ? (
                                                <Badge
                                                    count={dayEvs.length}
                                                    size="small"
                                                    offset={[-2, 2]}
                                                >
                                                    <Avatar
                                                        style={{
                                                            backgroundColor: isSel
                                                                ? "#2d4fd4"
                                                                : BaseColor,
                                                        }}
                                                    >
                                                        {value.format("DD")}
                                                    </Avatar>
                                                </Badge>
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: isSel
                                                            ? "#2d4fd4"
                                                            : BaseColor,
                                                    }}
                                                >
                                                    {value.format("DD")}
                                                </Avatar>
                                            )}
                                        </span>
                                    </Tooltip>
                                );
                            }}
                        />
                    )}
                </UICard>
            </Col>

            {/* ──────────────── Pannello destro ─────────────────────────────── */}
            <Col xs={24} lg={10}>

                {/* Form aggiunta — sempre visibile */}
                <UICard style={{ marginBottom: "16px" }}>
                    <UITitle level={4} color={BaseColor} style={{ marginBottom: "16px" }}>
                        Aggiungi post-it
                    </UITitle>

                    <Form layout="vertical" form={form} onFinish={handleAdd}>
                        <Form.Item
                            name="description"
                            label="Descrizione"
                            rules={[{
                                required: true,
                                message: "Inserisci la descrizione del post-it",
                            }]}
                        >
                            <UIInput placeholder="Es. Riunione, scadenza progetto…" />
                        </Form.Item>

                        <Form.Item
                            name="date"
                            label="Periodo"
                            rules={[{
                                required: true,
                                message: "Seleziona il periodo",
                            }]}
                        >
                            <CustomRangePicker
                                //@ts-ignore
                                showTime={false}
                                style={{ width: "100%" }}
                                format="DD/MM/YYYY"
                            />
                        </Form.Item>

                        <Row justify="end">
                            <Col>
                                <UIButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={isLoading}
                                >
                                    Aggiungi
                                </UIButton>
                            </Col>
                        </Row>
                    </Form>
                </UICard>

                {/* Dettaglio giorno — visibile solo se data selezionata */}
                {!!selectedDate && (
                    <UICard>
                        <UITitle level={4} color={BaseColor} style={{ marginBottom: "4px" }}>
                            {moment(selectedDate).format("DD MMMM YYYY")}
                        </UITitle>
                        <UIText style={{ color: "#9ca3af", fontSize: "13px" }}>
                            {selectedEvents.length} post-it
                        </UIText>

                        <Divider style={{ margin: "12px 0" }} />

                        {selectedEvents.length === 0 ? (
                            <UIText style={{ color: "#9ca3af", fontStyle: "italic" }}>
                                Nessun post-it per questo giorno.
                            </UIText>
                        ) : (
                            selectedEvents.map(ev => (
                                <EventRow key={ev.id}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <UIText style={{ fontWeight: 600, display: "block" }}>
                                            {ev.description}
                                        </UIText>
                                        <UIText style={{ fontSize: "12px", color: "#9ca3af" }}>
                                            {ev.date_from === ev.date_to
                                                ? moment(ev.date_from).format("DD/MM/YYYY")
                                                : `${moment(ev.date_from).format("DD/MM/YYYY")} — ${moment(ev.date_to).format("DD/MM/YYYY")}`
                                            }
                                        </UIText>
                                    </div>
                                    <Popconfirm
                                        title="Eliminare questo post-it?"
                                        okText="Sì, elimina"
                                        cancelText="Annulla"
                                        okButtonProps={{ danger: true }}
                                        onConfirm={() => handleDelete(ev.id)}
                                    >
                                        <UIButton
                                            icon={<DeleteOutlined />}
                                            loading={isLoading}
                                            style={{
                                                borderRadius: "7px",
                                                backgroundColor: "#e00102",
                                                borderColor: "#e00102",
                                                color: "white",
                                                flexShrink: 0,
                                            }}
                                        />
                                    </Popconfirm>
                                </EventRow>
                            ))
                        )}
                    </UICard>
                )}
            </Col>

            {/* ──────────────── Lista completa ──────────────────────────────── */}
            <Col xs={24}>
                <UICard>
                    <UITitle level={4} color={BaseColor} style={{ marginBottom: "16px" }}>
                        Tutti i post-it
                    </UITitle>

                    {isLoading ? (
                        <Skeleton active />
                    ) : events.length === 0 ? (
                        <UIText style={{ color: "#9ca3af", fontStyle: "italic" }}>
                            Nessun post-it presente.
                        </UIText>
                    ) : (
                        <>
                            {events
                                .slice()
                                .sort((a, b) => a.date_from.localeCompare(b.date_from))
                                .slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
                                .map(ev => (
                                    <AllEventsRow key={ev.id}>
                                        <Col flex="100px">
                                            <UIText style={{ fontSize: "13px", color: "#6b7280" }}>
                                                {moment(ev.date_from).format("DD/MM/YYYY")}
                                                {ev.date_from !== ev.date_to && (
                                                    <span style={{ display: "block" }}>
                                                        {moment(ev.date_to).format("DD/MM/YYYY")}
                                                    </span>
                                                )}
                                            </UIText>
                                        </Col>
                                        <Col flex="auto" style={{ minWidth: 0 }}>
                                            <UIText style={{ fontWeight: 500 }}>
                                                {ev.description}
                                            </UIText>
                                        </Col>
                                        <Col flex="none">
                                            <Popconfirm
                                                title="Eliminare questo post-it?"
                                                okText="Sì, elimina"
                                                cancelText="Annulla"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => handleDelete(ev.id)}
                                            >
                                                <UIButton
                                                    icon={<DeleteOutlined />}
                                                    loading={isLoading}
                                                    style={{
                                                        borderRadius: "7px",
                                                        backgroundColor: "#e00102",
                                                        borderColor: "#e00102",
                                                        color: "white",
                                                    }}
                                                />
                                            </Popconfirm>
                                        </Col>
                                    </AllEventsRow>
                                ))
                            }
                            {events.length > PAGE_SIZE && (
                                <Row justify="end" style={{ marginTop: "16px" }}>
                                    <Pagination
                                        current={currentPage}
                                        pageSize={PAGE_SIZE}
                                        total={events.length}
                                        onChange={setCurrentPage}
                                        showSizeChanger={false}
                                    />
                                </Row>
                            )}
                        </>
                    )}
                </UICard>
            </Col>

        </Row>
    );
};

// ─── Styled ───────────────────────────────────────────────────────────────────

const CustomAnnotazioniCalendar = styled(Calendar)`
    .back-arrow,
    .next-arrow {
        font-size: 20px;
        cursor: pointer;
        color: ${BaseColor};
    }
    .month-name {
        font-size: 16px;
        font-weight: bold;
        margin: 0 16px;
        text-transform: capitalize;
        color: ${BaseColor};
        display: inline-block;
        width: 160px;
        text-align: center;
    }
    .ant-picker-calendar-mini {
        border-radius: 7px !important;
    }
    .ant-picker-calendar-mini .ant-picker-panel {
        border-radius: 0 0 7px 7px !important;
    }
`;

const DayCell = styled.span<{ $selected?: boolean }>`
    color: ${p => (p.$selected ? BaseColor : "inherit")};
    font-weight: ${p => (p.$selected ? 700 : "normal")};
`;

const EventRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
    &:last-child {
        border-bottom: none;
    }
`;

const AllEventsRow = styled(Row).attrs({ gutter: [16, 0], align: "middle", wrap: false })`
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
    &:last-child {
        border-bottom: none;
    }
`;
